import {
  Container,
  Graphics,
  Text,
  TextStyle,
} from "../../../vendor/pixi.min.mjs";
import { BaseScene } from "../../scenes/BaseScene.js";
import { FroggerPlayer } from "./FroggerPlayer.js";
import { RowManager } from "./RowManager.js";
import { FroggerCollectibles } from "./FroggerCollectibles.js";
import { FroggerPickupBurst } from "./FroggerPickupBurst.js";
import { FroggerBirds } from "./FroggerBirds.js";
import { FroggerAbilities } from "./FroggerAbilities.js";
import { FroggerSkyLayer } from "./FroggerSkyLayer.js";
import { FroggerControls } from "./FroggerControls.js";
import { FroggerStartArea } from "./FroggerStartArea.js";
import { FroggerTopBar } from "./FroggerTopBar.js";
import { CollisionSystem } from "../../systems/CollisionSystem.js";
import { GameOverOverlay } from "../../ui/GameOverOverlay.js";
import { LevelCompleteOverlay } from "../../ui/LevelCompleteOverlay.js";
import { createThemeBackground } from "../../ui/createThemeBackground.js";
import { ScoreSubmitOverlay } from "../../ui/ScoreSubmitOverlay.js";
import { SceneChrome } from "../../ui/SceneChrome.js";
import { partyMode } from "../../ui/Confetti.js";
import { SceneAudio } from "../../core/SceneAudio.js";
import { t } from "../../config/i18n.js";
import { FROGGER_CONFIG } from "./froggerConfig.js";
import { FilterPipeline } from "../../systems/GameFilters.js";

const {
  CELL_SIZE,
  COLS,
  CAMERA_LERP,
  VISIBLE_BUFFER,
  INITIAL_LIVES,
  POINTS_PER_NEW_ROW,
  COIN_CHANCE,
  STAR_INTERVAL_RANGE,
  SPEED_MULTIPLIERS,
  ABILITY_SPAWN_CHANCE,
  START_SAFE_ROWS = 1,
  BOTTOM_BAR_H,
  PLAYER_SCROLL_CELLS_ABOVE_BOTTOM,
  FINISH_BUILDING_HEIGHT_PX = 1000,
} = FROGGER_CONFIG;

const PLAYER_START_ROW = Math.max(0, START_SAFE_ROWS - 1);

// Audio assets. Background ambience loops for the whole session;
// the coin/star cue is a one-shot fired on every collectible pickup.
const AMBIENCE_URL = "assets/frogger/sounds/background-ambiance.mp3";
const PICKUP_SFX_URL = "assets/frogger/sounds/coin-star.mp3";
const POWERUP_SFX_URL = "assets/frogger/sounds/powerups-sound.mp3";
const CAR_HORN_SFX_URL = "assets/frogger/sounds/car-horn.mp3";
// Played once when the balloon reaches the goal building (win).
const BALLOON_END_SFX_URL = "assets/frogger/sounds/baloon-end.mp3";
// Played on every successful balloon hop (any direction / any key).
const BALLOON_MOVE_SFX_URL = "assets/frogger/sounds/baloon-movement.mp3";

// Car-horn warning band, in cells ahead of the player. A car that
// enters this zone moving TOWARD the balloon honks once (with a
// random chance of a second beep). NEAR sits just past the hitbox
// so the honk reads as "about to hit you" rather than a collision;
// FAR is capped at ~1 cell so a car only honks when the player is
// right in front of it (one cell ahead) — not from further back.
const HORN_WARN_NEAR_CELLS = 0.1;
const HORN_WARN_FAR_CELLS = 1.0;
// Minimum gap (ms) between any two horn events so a busy lane can't
// turn into a wall of honking.
const HORN_COOLDOWN_MS = 600;

// Game-over highlight window: gameplay halts the moment the last
// life is lost, but we hold the world still for this many frames
// and pulse the killer + player so the player can see exactly what
// caused the death before the GameOverOverlay slides in. 60 fps ×
// 3s = 180 frames.
const GAME_OVER_HOLD_FRAMES = 180;
// Visible alpha range for the pulsing highlight. Stays opaque
// enough to read, ducks low enough to feel like an emergency blink.
const HIGHLIGHT_ALPHA_MIN = 0.35;
const HIGHLIGHT_ALPHA_MAX = 1.0;
// Sin frequency of the highlight pulse, in radians per millisecond.
// Tuned so ~4 visible blinks fit inside the 3-second hold:
// 4 cycles × 2π ÷ 3000 ms ≈ 0.0084 rad/ms.
const HIGHLIGHT_PULSE_SPEED = 0.0084;

const MSG_STYLE = new TextStyle({
  fontFamily: "Arial",
  fontSize: 48,
  fontWeight: "bold",
  fill: "#ef4444",
  align: "center",
});

/**
 * Endless scrolling Frogger with collectibles (coins + mandatory stars).
 */
export class FroggerScene extends BaseScene {
  /** @type {Container} Scrolls with camera */
  _worldContainer = null;

  /** @type {Container} Fixed HUD */
  _hudContainer = null;

  /** @type {FroggerSkyLayer|null} */
  _skyLayer = null;

  _themeBg = null;
  /**
   * Second copy of `frogger.background`, drawn ON TOP of the
   * worldContainer at low alpha + multiply blend so the backdrop
   * art reads as atmosphere over the gameplay (lanes, vehicles,
   * decor) without obscuring it. Pairs with the opaque lane fills
   * in {@link RowManager._drawRowBg} — together they prevent the
   * earlier visual bug where you could see the start-area grass
   * through the road lanes as the world scrolled.
   */
  _themeBgOverlay = null;

  _player = null;
  _rowManager = null;
  _collectibles = null;
  /** @type {FroggerPickupBurst|null} */
  _pickupBurst = null;
  /** @type {import('../../core/SceneAudio.js').SceneAudio|null} */
  _audio = null;
  /** Timestamp (performance.now) of the last car-horn event, for cooldown. */
  _lastHornAt = 0;
  _smoothMovement = false;
  _balloonTrails = false;

  _originX = 0;
  _cameraY = 0;
  _targetCameraY = 0;

  _mode = "infinite";
  _goalRow = 0;
  _finishGfx = null;
  _won = false;
  /** Active "balloon flies into the museum door" animation, or null. */
  _winFlight = null;

  _score = 0;
  _highestRow = 0;
  _lives = INITIAL_LIVES;
  _safeZonesReached = 0;
  _gameOver = false;
  _invincibleTimer = 0;

  _coinsCollected = 0;
  _starsCollected = 0;
  _starsMissed = 0;

  _elapsedTime = 0;

  // ── feature settings ────────────────────────────────
  _vehicleSpeed = "normal";
  _finishScoreGate = 200;
  _birdsEnabled = false;
  _birdTrails = false;
  _dashRowsMin = FROGGER_CONFIG.DASH_ROWS_RANGE[0];
  _dashRowsMax = FROGGER_CONFIG.DASH_ROWS_RANGE[1];
  _abilitiesEnabled = false;
  _laneChanging = false;
  _timeAttack = false;
  _timeAttackSeconds = 30;
  _timeAttackRemaining = 0;
  _slowTimeRemaining = 0;

  /** @type {import('./FroggerBirds.js').FroggerBirds|null} */
  _birds = null;

  /** @type {import('./FroggerAbilities.js').FroggerAbilities|null} */
  _abilities = null;

  _collectibleRows = new Set();
  _rowsSinceLastStar = 0;
  _nextStarIn = 0;

  /** @type {string[]} queued move directions, processed 1-per-frame */
  _moveQueue = [];

  _messageText = null;
  /** @type {SceneChrome|null} */
  _chrome = null;
  /** @type {FroggerControls|null} */
  _controls = null;
  /** @type {FroggerStartArea|null} */
  _startArea = null;
  /** @type {FroggerTopBar|null} */
  _topBar = null;
  _gameOverOverlay = null;
  _paused = false;

  // ── Death-freeze (game-over highlight phase) ──────────────────
  // When the player loses their last life we don't pop the
  // GameOverOverlay immediately — instead we hold the camera still
  // for `GAME_OVER_HOLD_FRAMES` frames and pulse the killer object
  // (vehicle / bird burst) plus the player so the user actually
  // sees what hit them. Time-up (no killer) gets the same hold
  // window with only the player pulsing for visual consistency.
  _gameOverPending = false;
  _gameOverHoldFrames = 0;
  _gameOverPendingArgs = null;
  /** @type {import('./FroggerVehicle.js').FroggerVehicle|null} */
  _gameOverKillerVehicle = null;

  /** @type {import('../../ui/LevelCompleteOverlay.js').LevelCompleteOverlay|null} */
  _winOverlay = null;

  /** @type {ScoreSubmitOverlay|null} */
  _scoreOverlay = null;

  _onSwipe = null;
  _onTap = null;

  // ── lifecycle ───────────────────────────────────────

  init(game) {
    super.init(game);

    this._mode = game.froggerMode || "fixed";
    this._won = false;

    const screenRows = Math.ceil(game.height / CELL_SIZE);
    this._goalRow = this._mode === "fixed" ? screenRows * 3 : Infinity;

    this._score = 0;
    this._highestRow = 0;
    this._lives = INITIAL_LIVES;
    this._gameOver = false;
    this._invincibleTimer = 90;
    this._safeZonesReached = 0;
    this._coinsCollected = 0;
    this._starsCollected = 0;
    this._starsMissed = 0;
    this._elapsedTime = 0;
    this._collectibleRows = new Set();
    this._rowsSinceLastStar = 0;
    this._nextStarIn = this._randInt(
      STAR_INTERVAL_RANGE[0],
      STAR_INTERVAL_RANGE[1],
    );

    this._originX = (game.width - COLS * CELL_SIZE) / 2;

    const gs = (key, def) =>
      game.theme?.getSetting("frogger.settings", key, def) ?? def;
    this._vehicleSpeed = gs("vehicleSpeed", "normal");
    this._vehicleDensity = gs("vehicleDensity", "normal");
    this._finishScoreGate = Number(gs("finishScoreGate", 200));
    this._bonusLives =
      gs("bonusLives", true) === true || gs("bonusLives", true) === "true";
    this._birdsEnabled =
      gs("birdsEnabled", false) === true ||
      gs("birdsEnabled", false) === "true";
    this._abilitiesEnabled =
      gs("abilitiesEnabled", false) === true ||
      gs("abilitiesEnabled", false) === "true";
    this._laneChanging =
      gs("laneChanging", false) === true ||
      gs("laneChanging", false) === "true";
    this._timeAttack =
      gs("timeAttack", false) === true || gs("timeAttack", false) === "true";
    this._timeAttackSeconds = Number(gs("timeAttackSeconds", 30));
    this._timeAttackRemaining = this._timeAttack
      ? this._timeAttackSeconds * 60
      : 0;
    this._slowTimeRemaining = 0;
    this._smoothMovement =
      gs("smoothMovement", false) === true ||
      gs("smoothMovement", false) === "true";
    this._balloonTrails =
      gs("balloonTrails", false) === true ||
      gs("balloonTrails", false) === "true";
    this._birdTrails =
      gs("birdTrails", false) === true || gs("birdTrails", false) === "true";
    this._dashRowsMin = Math.max(
      1,
      Number(gs("dashRowsMin", FROGGER_CONFIG.DASH_ROWS_RANGE[0])) ||
        FROGGER_CONFIG.DASH_ROWS_RANGE[0],
    );
    this._dashRowsMax = Math.max(
      this._dashRowsMin,
      Number(gs("dashRowsMax", FROGGER_CONFIG.DASH_ROWS_RANGE[1])) ||
        FROGGER_CONFIG.DASH_ROWS_RANGE[1],
    );

    this._createThemeBackground();

    this._skyLayer = new FroggerSkyLayer(game.width, game.height, game.theme);
    this.container.addChild(this._skyLayer.container);

    // Start area is split into two layers:
    //   • `container` (grass strip + road-blue underlay) — sits BEHIND the
    //     world so the player remains visible standing on it at game start.
    //   • `cardsContainer` (SPRINT / BOMBA / LASSITAS power-up cards) —
    //     sits ABOVE the world so scrolling lanes never cover the UI.
    this._startArea = new FroggerStartArea(this.game);
    this.container.addChild(this._startArea.container);

    this._worldContainer = new Container();
    this._hudContainer = new Container();
    this.container.addChild(this._worldContainer);

    this._createRowManager();
    this._createCollectibles();

    if (this._birdsEnabled) {
      this._birds = new FroggerBirds(
        this._originX,
        this.game.width,
        game.theme,
      );
      this._birds.setBurstEmitter(this._pickupBurst);
      this._birds.setTrailsEnabled(this._birdTrails);
      this._worldContainer.addChild(this._birds.container);
    }

    if (this._abilitiesEnabled) {
      this._abilities = new FroggerAbilities(this._originX, game.theme);
      this._abilities.setBurstEmitter(this._pickupBurst);
      this._worldContainer.addChild(this._abilities.container);
    }

    // Build the finish building AFTER the rows/vehicles so it visually
    // covers them — no lane strip can ever sneak in front of the museum's
    // base. The player is then added LAST so it still walks on top of
    // both the building and the lanes.
    if (this._mode === "fixed") {
      this._createFinishBuilding();
    }

    this._createPlayer();

    // Atmospheric overlay: a second copy of `frogger.background`
    // multiplied at 30 % alpha over the entire world (lanes,
    // start-area strip, finish building, vehicles, player). The
    // backdrop copy underneath provides the BASE colour for the
    // sky / margins; this layer adds the soft tint that ties the
    // whole stage to the chosen background art without obscuring
    // gameplay. Cards + HUD are added AFTER this so they stay
    // unaffected (sharp, full-colour UI).
    this._createThemeBackgroundOverlay();

    this.container.addChild(this._startArea.cardsContainer);
    this.container.addChild(this._hudContainer);

    this._topBar = new FroggerTopBar(this.game);
    this._hudContainer.addChild(this._topBar.container);

    this._createMessageText();
    this._chrome = new SceneChrome({
      game: this.game,
      host: this._hudContainer,
      overlayHost: this.container,
      gameId: "frogger",
      getHelp: () => this._buildHelp(),
      onPause: () => {
        this._paused = true;
      },
      onResume: () => {
        this._paused = false;
      },
    });
    this._chrome.attach();

    this._controls = new FroggerControls(this.game, (dir) => {
      if (this._gameOver || this._won || this._paused) return;
      this._moveQueue.push(dir);
    });
    this._hudContainer.addChild(this._controls.container);

    this._bindInput();

    this._targetCameraY = this._calcCameraTarget();
    this._cameraY = this._targetCameraY;
    this._worldContainer.y = this._cameraY;

    this._refreshVisibleRows();
    this._updateHud();

    // Cross-game post-process filter stack (CRT, Old Film, Pixelate,
    // Tilt-Shift, God Rays). Attached to the scene root so the
    // entire stage — world, HUD, overlays — passes through it.
    // No-op if every shared filter toggle is OFF (the default).
    this._filterFx = new FilterPipeline(this.game, this.container);

    // Audio: looping background ambience starts immediately (with
    // an autoplay-policy gesture fallback baked into SceneAudio).
    // The coin/star pickup cue is pre-warmed here too so its first
    // play has no load hitch.
    this._audio = new SceneAudio();
    this._audio.playLoop(AMBIENCE_URL, 0.32);
  }

  _createThemeBackground() {
    // Temporarily disabled. Previously this method painted the
    // `frogger.background` image as a full-screen backdrop so the
    // sky-region above the topmost road row and the margins
    // outside the playable grid would show the user's art. It
    // worked in isolation but pairs poorly with the multiply
    // overlay below — the combined look ended up noticeably dark
    // and washed out, so both layers are off until we have a
    // proper per-slot brightness/blend control. The field stays
    // initialised to null so destroy() and other null-checks
    // keep working unchanged.
    this._themeBg = null;
  }

  /**
   * Atmospheric multiply-blend overlay copy of the background image.
   *
   * DISABLED. A 30 % alpha multiply-blend pass over the world was
   * intended to tint lane colours toward the chosen background art,
   * but in practice it dragged the entire stage darker (multiply by
   * any sub-white colour reduces brightness) and made gameplay hard
   * to read. Until we add a per-game intensity control + a brighter
   * default blend (e.g. soft-light), this stays a no-op so the
   * balloon stage looks exactly like the pre-background build.
   */
  _createThemeBackgroundOverlay() {
    this._themeBgOverlay = null;
  }

  _createMessageText() {
    this._messageText = new Text({ text: "", style: MSG_STYLE });
    this._messageText.anchor.set(0.5);
    this._messageText.x = this.game.width / 2;
    this._messageText.y = this.game.height / 2;
    this._messageText.visible = false;
    this._hudContainer.addChild(this._messageText);

    this._slowTimeText = new Text({
      text: "",
      style: new TextStyle({
        fontFamily: "Arial",
        fontSize: 18,
        fontWeight: "bold",
        fill: "#a855f7",
      }),
    });
    this._slowTimeText.x = 20;
    this._slowTimeText.y = FROGGER_CONFIG.TOP_BAR_H + 6;
    this._slowTimeText.visible = false;
    this._hudContainer.addChild(this._slowTimeText);
  }

  update(delta) {
    if (!this._finishPositioned) this._positionFinishSprite();

    if (this._won) {
      if (this._skyLayer) this._skyLayer.update(delta);
      if (this._winFlight) {
        this._updateWinFlight(delta);
      } else if (this._winOverlay) {
        this._winOverlay.update(delta);
      }
      return;
    }
    if (this._gameOver) {
      if (this._gameOverPending) {
        this._tickGameOverHighlight(delta);
      } else if (this._gameOverOverlay) {
        this._gameOverOverlay.update(delta);
      }
      return;
    }
    if (this._paused) return;

    if (this._skyLayer) this._skyLayer.update(delta);

    if (this._moveQueue.length > 0) {
      const dir = this._moveQueue.shift();
      if (this._moveQueue.length > 3) this._moveQueue.length = 3;
      // Snapshot the cell the balloon is about to leave so the
      // optional balloon-trail confetti can spawn there post-move.
      const prevCol = this._player.gridCol;
      const prevRow = this._player.worldRow;
      let moved = false;
      if (
        this._mode === "fixed" &&
        dir === "up" &&
        this._player.worldRow + 1 >= this._goalRow
      ) {
        this._player.move(0, 1);
        moved = true;
      } else {
        switch (dir) {
          case "up":
            moved = this._player.move(0, 1);
            break;
          case "down":
            moved = this._player.move(0, -1);
            break;
          case "left":
            moved = this._player.move(-1, 0);
            break;
          case "right":
            moved = this._player.move(1, 0);
            break;
        }
      }
      if (moved) {
        // Hop sound — fires on every successful move in any
        // direction (keyboard or swipe both feed _moveQueue).
        this._audio?.playOneShot(BALLOON_MOVE_SFX_URL, 0.5);
        if (this._balloonTrails && this._pickupBurst) {
          // Spawn the wake at the cell the balloon just left, but
          // bias every particle's initial velocity toward the new
          // cell so the whole cloud drifts after the balloon for
          // a few frames before drag brings it to rest — i.e. the
          // confetti has inertia, it doesn't sit on the old cell.
          const dx = this._player.gridCol - prevCol;
          const dy = this._player.worldRow - prevRow;
          const BIAS_SPEED = 3.5; // px/frame applied to every particle
          const bias = {
            x: Math.sign(dx) * BIAS_SPEED,
            // World Y is INVERTED relative to worldRow (higher
            // row = more-negative y), so flip the sign here.
            y: -Math.sign(dy) * BIAS_SPEED,
          };
          const x = this._originX + prevCol * CELL_SIZE + CELL_SIZE / 2;
          const y = -prevRow * CELL_SIZE + CELL_SIZE / 2;
          this._pickupBurst.burst(x, y, "balloonTrail", null, bias);
        }
        this._onPlayerMoved();
      }
    }

    this._elapsedTime += delta / 60;

    if (this._timeAttack) {
      this._timeAttackRemaining -= delta;
      if (this._timeAttackRemaining <= 0) {
        this._timeAttackRemaining = 0;
        this._setGameOver(true);
        return;
      }
    }

    if (this._slowTimeRemaining > 0) {
      this._slowTimeRemaining -= delta;
      this._rowManager.setLiveSpeedFactor(FROGGER_CONFIG.SLOWTIME_FACTOR);
      if (this._slowTimeRemaining <= 0) {
        this._rowManager.setLiveSpeedFactor(1.0);
      }
    }

    this._targetCameraY = this._calcCameraTarget();
    this._cameraY += (this._targetCameraY - this._cameraY) * CAMERA_LERP;
    this._worldContainer.y = this._cameraY;

    if (this._startArea) this._startArea.update(this._player.worldRow);

    let { lowRow, highRow } = this._visibleRange();
    if (this._mode === "fixed") {
      // The goal row itself is drawn as a clean safe lane at the
      // building's base; nothing is generated past it so the area
      // behind the building stays as plain sky.
      highRow = Math.min(highRow, this._goalRow);
    }

    this._rowManager.setPlayerExclusion(
      this._player.x,
      this._player.hitbox.width,
    );
    this._rowManager.ensureRows(lowRow, highRow);
    this._rowManager.updateVisibleRows(lowRow, highRow, delta);
    this._rowManager.cleanupOutside(lowRow - 5, highRow + 5);
    this._collectibles.cleanupOutside(lowRow - 10, highRow + 5);
    if (this._abilities)
      this._abilities.cleanupOutside(lowRow - 10, highRow + 5);

    this._player.update(delta);
    this._collectibles.pulse(this._player.worldRow);
    if (this._pickupBurst) this._pickupBurst.update(delta);

    this._cleanupMissedStars();

    if (this._invincibleTimer > 0) {
      this._invincibleTimer -= delta;
      this._rowManager.clearOverlapping(
        this._player.getHitRect(),
        this._player.worldRow,
      );
      this._player.display.alpha =
        Math.sin(this._invincibleTimer * 0.5) > 0 ? 1 : 0.3;
      if (this._invincibleTimer <= 0) {
        this._player.display.alpha = 1;
      }
    } else if (this._rowManager.isSafeRow(this._player.worldRow)) {
      // no collision on safe zones
    } else {
      const vehicles = this._rowManager.getVisibleVehicles(
        this._player.worldRow,
        this._player.worldRow,
      );
      const playerRect = this._player.getHitRect();
      const gridLeft = this._originX;
      const gridRight = this._originX + COLS * CELL_SIZE;
      let hitVehicle = null;
      for (const v of vehicles) {
        if (!v.active) continue;
        if (v.isDrifting) continue;
        const vr = v.getHitRect();
        if (vr.x + vr.w < gridLeft || vr.x > gridRight) continue;
        if (CollisionSystem.checkRects(playerRect, vr)) {
          hitVehicle = v;
          break;
        }
      }
      if (hitVehicle) {
        const vr = hitVehicle.getHitRect();
        const distX = Math.abs(
          playerRect.x + playerRect.w / 2 - (vr.x + vr.w / 2),
        );
        console.warn(
          `[FROGGER HIT] VEHICLE | row=${this._player.worldRow} col=${this._player.gridCol}` +
            ` | player={x:${Math.round(playerRect.x)},y:${Math.round(playerRect.y)},w:${playerRect.w},h:${playerRect.h}}` +
            ` | vehicle={x:${Math.round(vr.x)},y:${Math.round(vr.y)},w:${vr.w},h:${vr.h}}` +
            ` | distX=${Math.round(distX)} type=${hitVehicle._vehicleType}` +
            ` | drifting=${hitVehicle.isDrifting} dir=${hitVehicle.direction}` +
            ` | gridBounds=[${Math.round(gridLeft)},${Math.round(gridRight)}]` +
            ` | vehiclesOnRow=${vehicles.length}`,
        );
        // Hand the live vehicle to `_onPlayerHit` so the
        // game-over highlight loop can pulse the right
        // sprite. The vehicle keeps moving in subsequent
        // frames otherwise (only its updates stop once
        // `_gameOver` flips), but stashing the reference
        // here freezes our pointer to the actual killer.
        this._onPlayerHit("vehicle", { vehicle: hitVehicle });
      } else {
        // No collision this frame — check for a near-miss so
        // an approaching car can honk at the balloon before
        // it actually hits.
        this._checkCarHornWarning(vehicles, playerRect);
      }
    }

    if (this._birds) {
      this._birds.update(delta, this._player.worldRow);
      if (this._invincibleTimer <= 0) {
        const playerRect = this._player.getHitRect();
        const birdHit = this._birds.checkCollisionDebug(playerRect);
        if (birdHit) {
          console.warn(
            `[FROGGER HIT] BIRD | row=${this._player.worldRow} col=${this._player.gridCol}` +
              ` | player={x:${Math.round(playerRect.x)},y:${Math.round(playerRect.y)}}` +
              ` | bird={x:${Math.round(birdHit.x)},y:${Math.round(birdHit.y)}}` +
              ` | distX=${Math.round(Math.abs(playerRect.x - birdHit.x))}` +
              ` | distY=${Math.round(Math.abs(playerRect.y - birdHit.y))}`,
          );
          // Birds destroy their own sprite inside the
          // collision check, so we only have a position
          // to work with — pass it through so the
          // game-over phase can spawn an impact burst at
          // that exact world point.
          this._onPlayerHit("bird", { x: birdHit.x, y: birdHit.y });
        }
      }
    }

    if (this._abilities) {
      this._abilities.pulse(this._player.worldRow);
    }

    this._updateHud();

    if (this._filterFx) {
      // Tilt-Shift: keep the in-focus disc on the balloon. The
      // world container scrolls vertically with the camera, so
      // the player's screen Y is `cameraY + display.y` while X
      // sits in worldContainer space (no horizontal scroll).
      if (this._player?.display) {
        const halfCell = CELL_SIZE / 2;
        const sx = this._player.display.x + halfCell;
        const sy = this._cameraY + this._player.display.y + halfCell;
        this._filterFx.setFocus(sx / this.game.width, sy / this.game.height);
      }
      this._filterFx.update(delta);
    }
  }

  destroy() {
    const input = this.game?.input;
    if (input) {
      if (this._onSwipe) input.off("swipe", this._onSwipe);
      if (this._onTap) input.off("tap", this._onTap);
    }
    if (this._filterFx) {
      this._filterFx.destroy();
      this._filterFx = null;
    }
    if (this._chrome) {
      this._chrome.destroy();
      this._chrome = null;
    }
    if (this._controls) {
      this._controls.destroy();
      this._controls = null;
    }
    if (this._topBar) {
      this._topBar.destroy();
      this._topBar = null;
    }
    if (this._startArea) {
      this._startArea.destroy();
      this._startArea = null;
    }
    if (this._gameOverOverlay) {
      this._gameOverOverlay.destroy();
      this._gameOverOverlay = null;
    }
    if (this._winOverlay) {
      this._winOverlay.destroy();
      this._winOverlay = null;
    }
    if (this._scoreOverlay) {
      this._scoreOverlay.destroy();
      this._scoreOverlay = null;
    }
    if (this._rowManager) this._rowManager.destroy();
    if (this._player) this._player.destroy();
    if (this._collectibles) this._collectibles.destroy();
    if (this._pickupBurst) {
      this._pickupBurst.destroy();
      this._pickupBurst = null;
    }
    if (this._birds) {
      this._birds.destroy();
      this._birds = null;
    }
    if (this._abilities) {
      this._abilities.destroy();
      this._abilities = null;
    }
    if (this._audio) {
      this._audio.destroy();
      this._audio = null;
    }
    this._skyLayer = null;
    this._themeBg = null;
    this._themeBgOverlay = null;
    this._onSwipe = null;
    this._onTap = null;
    this._paused = false;
    this._won = false;
    this._finishGfx = null;
    super.destroy();
  }

  // ── setup ──────────────────────────────────────────

  _createRowManager() {
    this._rowManager = new RowManager(
      this._originX,
      this.game.width,
      (worldRow, type) => this._onRowGenerated(worldRow, type),
      this._mode === "fixed" ? this._goalRow : Infinity,
      this.game.theme,
    );
    this._rowManager.setSpeedMultiplier(
      SPEED_MULTIPLIERS[this._vehicleSpeed] ?? 1.0,
    );
    this._rowManager.setDensityProfile(this._vehicleDensity);
    if (this._laneChanging) this._rowManager.enableLaneChanging(true);
    this._worldContainer.addChild(this._rowManager.container);
  }

  _createCollectibles() {
    this._collectibles = new FroggerCollectibles(
      this._originX,
      this.game.theme,
    );
    this._worldContainer.addChild(this._collectibles.container);
    this._pickupBurst = new FroggerPickupBurst();
    this._worldContainer.addChild(this._pickupBurst.container);
  }

  _createPlayer() {
    const startCol = Math.floor(COLS / 2);
    this._player = new FroggerPlayer(
      startCol,
      PLAYER_START_ROW,
      this._originX,
      this.game.theme,
    );
    this._player.setSmoothMovement(this._smoothMovement);
    this._worldContainer.addChild(this._player.display);
  }

  // (Top stats now live in FroggerTopBar; see _createMessageText above for
  // the floating midscreen message + slow-time pill.)

  _buildHelp() {
    const modeGoal =
      this._mode === "fixed"
        ? t("frogger.help_goal_fix", { v: this._goalRow })
        : t("frogger.help_goal_inf");

    const title =
      this._mode === "fixed"
        ? t("frogger.help_title_fix")
        : t("frogger.help_title_inf");

    return {
      title,
      sections: [
        { heading: t("help.goal"), color: 0xfbbf24, body: modeGoal },
        {
          heading: t("help.controls"),
          color: 0xfbbf24,
          body: t("frogger.help_controls"),
        },
        {
          heading: t("help.lives"),
          color: 0xef4444,
          body: t("frogger.help_lives"),
        },
        {
          heading: t("help.invincibility"),
          color: 0xa855f7,
          body: t("frogger.help_invincibility"),
        },
        {
          heading: t("help.coins"),
          color: 0xfbbf24,
          body: t("frogger.help_coins"),
        },
        {
          heading: t("help.stars"),
          color: 0xec4899,
          body: t("frogger.help_stars"),
        },
        {
          heading: t("help.scoring"),
          color: 0x22c55e,
          body: t("frogger.help_scoring"),
        },
      ],
    };
  }

  _bindInput() {
    const input = this.game.input;

    this._onSwipe = (e) => {
      if (this._gameOver || this._won || this._paused) return;
      this._moveQueue.push(e.direction);
    };

    this._onTap = () => {
      // Don't allow the tap-to-restart while the death-freeze
      // is still pulsing the killer + balloon — otherwise the
      // 3-second highlight gets cut short on the very first
      // accidental tap.
      if (this._gameOver && !this._gameOverPending) this._restartGame();
    };

    input.on("swipe", this._onSwipe);
    input.on("tap", this._onTap);
  }

  // ── camera ─────────────────────────────────────────

  /**
   * Camera target so the player is always rendered between the top stat
   * bar and the bottom strip.
   *
   * - While the player is still in the start safety zone, the world is held
   *   still: row 0 sits on top of the bottom strip, the player walks up the
   *   grass area (in real screen pixels, no scroll).
   * - Once the player crosses out of the safety zone, the camera locks the
   *   player at a fixed screen Y (just above the bottom bar) and the world
   *   scrolls underneath them.
   *
   * The two phases meet exactly at row == START_SAFE_ROWS — no visual jump.
   */
  _calcCameraTarget() {
    const screenH = this.game.height;
    // Fixed screen Y the player snaps to once we're scrolling.
    const playerScrollY =
      screenH - BOTTOM_BAR_H - PLAYER_SCROLL_CELLS_ABOVE_BOTTOM * CELL_SIZE;
    // Rest position: row 0 sits exactly START_SAFE_ROWS below the lock point.
    const restCameraY = playerScrollY + START_SAFE_ROWS * CELL_SIZE;
    // Dynamic position: as the player climbs, camera climbs with them.
    const scrollCameraY = playerScrollY + this._player.worldRow * CELL_SIZE;
    return Math.max(restCameraY, scrollCameraY);
  }

  _visibleRange() {
    const screenH = this.game.height;
    const camY = this._cameraY;
    const topWorldY = -camY;
    const bottomWorldY = -camY + screenH;
    const highRow = Math.ceil(-topWorldY / CELL_SIZE) + VISIBLE_BUFFER;
    const lowRow = Math.max(
      0,
      Math.floor(-bottomWorldY / CELL_SIZE) - VISIBLE_BUFFER,
    );
    return { lowRow, highRow };
  }

  _refreshVisibleRows() {
    let { lowRow, highRow } = this._visibleRange();
    if (this._mode === "fixed") {
      // Mirror the cap from update(): never generate rows past the
      // goal row, otherwise the safe-lane texture bleeds in behind
      // the museum building.
      highRow = Math.min(highRow, this._goalRow);
    }
    this._rowManager.ensureRows(lowRow, highRow);
  }

  // ── collectible placement (called by RowManager) ───

  _onRowGenerated(worldRow, type) {
    if (this._collectibleRows.has(worldRow)) return;
    this._collectibleRows.add(worldRow);

    if (type !== "road") return;

    let abilityPlaced = false;
    if (this._abilities && Math.random() < ABILITY_SPAWN_CHANCE) {
      const col = Math.floor(Math.random() * COLS);
      this._abilities.addAbility(worldRow, col);
      abilityPlaced = true;
    }

    if (!abilityPlaced && Math.random() < COIN_CHANCE) {
      const col = Math.floor(Math.random() * COLS);
      this._collectibles.addCoin(worldRow, col);
    }

    this._rowsSinceLastStar++;
    if (this._rowsSinceLastStar >= this._nextStarIn) {
      const col = Math.floor(Math.random() * COLS);
      this._collectibles.addStar(worldRow, col);
      this._rowsSinceLastStar = 0;
      this._nextStarIn = this._randInt(
        STAR_INTERVAL_RANGE[0],
        STAR_INTERVAL_RANGE[1],
      );
    }
  }

  // ── game logic ─────────────────────────────────────

  _onPlayerMoved() {
    const col = this._player.gridCol;
    const row = this._player.worldRow;

    const item = this._collectibles.checkCollection(col, row);
    if (item) {
      this._score += item.points;
      if (item.type === "coin") this._coinsCollected++;
      if (item.type === "star") this._starsCollected++;
      this._spawnPickupBurst(col, row, item.type);
      // Coin + star share the same pickup chime per the asset
      // the client supplied ("coin-star-all").
      this._audio?.playOneShot(PICKUP_SFX_URL, 0.9);
    }

    if (this._abilities) {
      const ability = this._abilities.checkCollection(col, row);
      if (ability) {
        this._audio?.playOneShot(POWERUP_SFX_URL, 0.9);
        this._activateAbility(ability.type);
      }
    }

    if (row > this._highestRow) {
      const newRows = row - this._highestRow;
      this._score += newRows * POINTS_PER_NEW_ROW;
      this._highestRow = row;

      if (this._rowManager.isSafeRow(row)) {
        this._safeZonesReached++;
        if (this._bonusLives && this._safeZonesReached % 2 === 0) {
          this._lives++;
        }
      }
    }

    if (this._mode === "fixed" && row >= this._goalRow && !this._won) {
      this._onWin();
      return;
    }

    this._updateHud();
    this._refreshVisibleRows();
  }

  /**
   * Fire a small confetti puff at the centre of (col, row) — world-space
   * coordinates that match `FroggerCollectibles._positionGfx`. The burst
   * container lives inside `_worldContainer`, so the puff scrolls with
   * the camera and stays glued to the cell where the pickup happened.
   */
  _spawnPickupBurst(col, row, kind) {
    if (!this._pickupBurst) return;
    const x = this._originX + col * CELL_SIZE + CELL_SIZE / 2;
    const y = -row * CELL_SIZE + CELL_SIZE / 2;
    this._pickupBurst.burst(x, y, kind);
  }

  /**
   * Sound the car horn when a vehicle is bearing down on the
   * balloon. A car counts as "about to hit" when it shares the
   * player's row, is moving TOWARD the player, and its leading
   * edge has entered the warning band [NEAR, FAR] cells ahead of
   * the player's hitbox (just past the hitbox, so it fires before
   * the actual collision rather than on top of it).
   *
   * Per-vehicle `_honkedAtPlayer` latch makes each car honk only
   * ONCE per approach (not every frame it's in the zone); the
   * latch clears when the car leaves the band, and pooled
   * recycling self-heals because a freshly-spawned car starts far
   * away (outside the band → latch false). A global cooldown on
   * top keeps a packed lane from becoming a wall of honking.
   *
   * @param {import('./FroggerVehicle.js').FroggerVehicle[]} vehicles
   * @param {{x:number,y:number,w:number,h:number}} playerRect
   */
  _checkCarHornWarning(vehicles, playerRect) {
    if (!vehicles || vehicles.length === 0) return;
    const near = CELL_SIZE * HORN_WARN_NEAR_CELLS;
    const far = CELL_SIZE * HORN_WARN_FAR_CELLS;
    const playerCx = playerRect.x + playerRect.w / 2;
    const playerLeft = playerRect.x;
    const playerRight = playerRect.x + playerRect.w;

    for (const v of vehicles) {
      if (!v.active || v.isDrifting) continue;
      const vr = v.getHitRect();
      const vCx = vr.x + vr.w / 2;

      let approaching = false;
      let gap = Infinity;
      if (v.direction > 0) {
        // Moving right — its front is its right edge. It's
        // approaching only if it's still to the LEFT of the
        // player; gap = space between car front and player.
        approaching = vCx < playerCx;
        gap = playerLeft - (vr.x + vr.w);
      } else {
        // Moving left — front is its left edge; approaching
        // when it's to the RIGHT of the player.
        approaching = vCx > playerCx;
        gap = vr.x - playerRight;
      }

      const inZone = approaching && gap >= near && gap <= far;
      if (inZone) {
        if (!v._honkedAtPlayer) {
          v._honkedAtPlayer = true;
          this._honkCar();
        }
      } else {
        v._honkedAtPlayer = false;
      }
    }
  }

  /**
   * Fire the car-horn cue — once, with a coin-flip chance of a
   * quick second beep so it varies between a single "beep" and a
   * double "be-beep". Rate-limited by {@link HORN_COOLDOWN_MS}.
   */
  _honkCar() {
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - this._lastHornAt < HORN_COOLDOWN_MS) return;
    this._lastHornAt = now;
    this._audio?.playOneShot(CAR_HORN_SFX_URL, 0.4);
    if (Math.random() < 0.5) {
      const delay = 240 + Math.random() * 140;
      setTimeout(() => {
        // Guard: scene may have been torn down (audio nulled
        // in destroy) before the second beep lands.
        this._audio?.playOneShot(CAR_HORN_SFX_URL, 0.4);
      }, delay);
    }
  }

  /** Remove stars the player has passed — no penalty, just cleanup. */
  _cleanupMissedStars() {
    const missed = this._collectibles.getMissedStars(this._player.worldRow);
    if (missed.length === 0) return;
    this._collectibles.removeMissedStars(this._player.worldRow);
    this._starsMissed += missed.length;
  }

  _activateAbility(type) {
    const { SLOWTIME_DURATION, BOMB_RADIUS } = FROGGER_CONFIG;

    switch (type) {
      case "dash": {
        const startCol = this._player.gridCol;
        const startRow = this._player.worldRow;
        // Randomised dash distance — the balloon rockets a random
        // number of rows forward within the user-tunable [min, max]
        // range (Options → Balloon → Dash Min/Max Rows), picking a
        // different jump every time the ability fires.
        const dashRows = this._randInt(this._dashRowsMin, this._dashRowsMax);
        const targetRow = startRow + dashRows;

        // Spray a cyan exhaust trail spanning the rows the dash skips
        // over — looks like the balloon shot up and left a wake behind.
        for (let r = startRow; r < targetRow; r++) {
          this._spawnPickupBurst(startCol, r, "dash");
        }

        // Plow through traffic on EVERY row the balloon passes through.
        // Each destroyed vehicle pops a red bomb-style burst at its
        // exact world position — so the explosions track the cars
        // we hit, not the lane we end up on.
        for (let r = startRow + 1; r <= targetRow; r++) {
          const cleared = this._rowManager.clearRow(r);
          if (this._pickupBurst) {
            for (const p of cleared) {
              this._pickupBurst.burst(p.x, p.y, "bomb");
            }
          }
        }

        this._player.setPosition(startCol, targetRow);
        this._invincibleTimer = Math.max(this._invincibleTimer, 30);
        this._onPlayerMoved();
        break;
      }
      case "slowtime": {
        this._slowTimeRemaining = SLOWTIME_DURATION;
        this._spawnPickupBurst(
          this._player.gridCol,
          this._player.worldRow,
          "slowtime",
        );
        break;
      }
      case "bomb": {
        const row = this._player.worldRow;
        const col = this._player.gridCol;
        /** @type {Array<{x: number, y: number}>} */
        const blastPoints = [];
        for (let r = row - BOMB_RADIUS; r <= row + BOMB_RADIUS; r++) {
          const cleared = this._rowManager.clearRow(r);
          for (const p of cleared) blastPoints.push(p);
        }
        if (blastPoints.length > 0 && this._pickupBurst) {
          // One red blast at the centre of each destroyed vehicle —
          // the explosions visibly track where the cars disappeared
          // from, not where the balloon happens to be standing.
          for (const p of blastPoints) {
            this._pickupBurst.burst(p.x, p.y, "bomb");
          }
        } else {
          // Bomb detonated on empty rows (no traffic to wipe out) —
          // still give the player a confirmation puff at the balloon.
          this._spawnPickupBurst(col, row, "bomb");
        }
        break;
      }
    }
  }

  _onPlayerHit(source = "unknown", killer = null) {
    this._lives--;
    this._moveQueue.length = 0;
    console.warn(
      `[FROGGER LIFE LOST] source=${source} | lives=${this._lives}` +
        ` | row=${this._player.worldRow} col=${this._player.gridCol}` +
        ` | invincible=${this._invincibleTimer > 0}`,
    );
    if (this._lives <= 0) {
      console.warn(`[FROGGER GAME OVER] Final source=${source}`);
    }
    this._updateHud();

    if (this._lives <= 0) {
      // Pass the killer through so the death-freeze phase
      // pulses the right object. `killer` is `{ vehicle }` for
      // a vehicle hit (live FroggerVehicle reference) and
      // `{ x, y }` for a bird hit (the bird sprite is already
      // destroyed by checkCollisionDebug, so we keep just its
      // last position for the impact burst).
      this._setGameOver(false, killer);
      return;
    }

    const safeRow = this._rowManager.findLastSafeRow(this._player.worldRow);
    console.warn(
      `[FROGGER RESPAWN] teleport from row ${this._player.worldRow} to safe row ${safeRow}`,
    );
    this._player.setPosition(Math.floor(COLS / 2), safeRow);
    this._invincibleTimer = 90;
  }

  /**
   * Triggered the moment the player runs out of lives (or the
   * time-attack timer hits zero). Halts gameplay immediately and
   * starts a 3-second "death freeze" during which the killer
   * object + the player pulse red so the user can see exactly
   * what hit them. After the hold elapses, `_showGameOverOverlay`
   * pops the actual modal.
   *
   * `killer` shape:
   *   { vehicle: FroggerVehicle } — vehicle collisions; live ref
   *                                 used to pulse the sprite.
   *   { x, y }                    — bird collisions; the bird's
   *                                 sprite is already destroyed,
   *                                 so we just remember the
   *                                 impact point for the burst.
   *   null                        — time-up; only the player
   *                                 pulses (no specific killer).
   */
  _setGameOver(timeUp = false, killer = null) {
    this._gameOver = true;
    this._messageText.visible = false;
    // Stash everything `_showGameOverOverlay` will need so the
    // build-stats / build-actions logic only runs once at the
    // end of the freeze, not at trigger time.
    this._gameOverPendingArgs = { timeUp };
    this._gameOverPending = true;
    this._gameOverHoldFrames = GAME_OVER_HOLD_FRAMES;
    this._gameOverKillerVehicle = killer?.vehicle || null;

    // Spawn an impact burst at the collision point so the
    // freeze opens with a visible "BANG". Vehicles use their
    // hit-rect centre; birds carry their last (x, y); time-up
    // gets nothing — the timer ran out elsewhere on the HUD.
    if (this._pickupBurst) {
      if (killer?.vehicle?.getHitRect) {
        const r = killer.vehicle.getHitRect();
        this._pickupBurst.burst(r.x + r.w / 2, r.y + r.h / 2, "bomb");
      } else if (
        killer &&
        Number.isFinite(killer.x) &&
        Number.isFinite(killer.y)
      ) {
        this._pickupBurst.burst(killer.x, killer.y, "bomb");
      }
    }
  }

  /**
   * Per-frame highlight loop while `_gameOverPending` is true.
   * Pulses the player + the killer vehicle (if any) between
   * `HIGHLIGHT_ALPHA_MIN` and `HIGHLIGHT_ALPHA_MAX` so the user's
   * eye is drawn to the cause of death. When the hold timer hits
   * zero, restores opacities and pops the actual GameOverOverlay.
   */
  _tickGameOverHighlight(delta) {
    this._gameOverHoldFrames -= delta;

    // Drive both targets off the same wall-clock phase so the
    // killer + the player blink in sync — reads as a single
    // "this is the deadly pair" highlight rather than two
    // unrelated objects ducking randomly.
    const phase = performance.now() * HIGHLIGHT_PULSE_SPEED;
    const t01 = (Math.sin(phase) + 1) * 0.5;
    const alpha =
      HIGHLIGHT_ALPHA_MIN + (HIGHLIGHT_ALPHA_MAX - HIGHLIGHT_ALPHA_MIN) * t01;

    if (this._player?.display) {
      this._player.display.alpha = alpha;
    }
    const veh = this._gameOverKillerVehicle;
    if (veh?.display) {
      veh.display.alpha = alpha;
    }

    // Keep the impact-burst particles spawned in `_setGameOver`
    // animating — without this the puff would freeze mid-flight
    // because the main update path returns early on `_gameOver`.
    if (this._pickupBurst) this._pickupBurst.update(delta);

    if (this._gameOverHoldFrames > 0) return;

    // Hold complete — restore alpha and let the overlay take
    // over. Hiding the HUD here (not at trigger time) keeps the
    // score / lives / time readout visible during the freeze
    // so the player can see the "0 lives" tally that earned
    // them the game over.
    if (this._player?.display) this._player.display.alpha = 1;
    if (veh?.display) veh.display.alpha = 1;
    this._gameOverPending = false;
    this._gameOverKillerVehicle = null;
    this._showGameOverOverlay(this._gameOverPendingArgs || {});
    this._gameOverPendingArgs = null;
  }

  /** Build + show the DOM Game Over modal. Split out of
   *  `_setGameOver` so the highlight phase can run first. */
  _showGameOverOverlay({ timeUp = false } = {}) {
    this._hudContainer.visible = false;

    const title = timeUp ? t("frogger.time_up") : t("common.game_over");
    const theme = this.game.theme;

    const stats = [
      {
        icon: theme?.get("frogger.hud.score"),
        label: t("common.stat_score"),
        value: this._score,
      },
      {
        icon: theme?.get("frogger.collectible.coin"),
        label: t("common.stat_coins"),
        value: this._coinsCollected,
      },
      {
        icon: theme?.get("frogger.collectible.star"),
        label: t("common.stat_stars"),
        value: this._starsCollected,
      },
    ];

    const actions = [
      {
        kind: "home",
        label: t("common.main_menu"),
        onClick: () => this.game.sceneManager.switchScene("menu"),
      },
      {
        kind: "retry",
        label: t("common.retry"),
        primary: true,
        onClick: () => this._restartGame(),
      },
      {
        kind: "next",
        label: t("common.next"),
        onClick: () => this._showScoreOverlay(this._score),
      },
    ];

    this._gameOverOverlay?.destroy();
    this._gameOverOverlay = new GameOverOverlay();
    this._gameOverOverlay.show(this.game.width, this.game.height, {
      title,
      won: false,
      stats,
      actions,
    });
    this.container.addChild(this._gameOverOverlay.container);
  }

  _restartGame() {
    this._moveQueue.length = 0;
    this.destroy();
    this.container.removeChildren();
    this.init(this.game);
  }

  _createFinishBuilding() {
    const cs = CELL_SIZE;
    const bw = COLS * cs;
    // Y of the goal-row LINE (world). The building bottom always sits here so the
    // image never bleeds down onto the active road lanes.
    const goalLineY = -this._goalRow * cs;

    // Try the themed sprite first (muzeum.png by default). `cover` fit
    // scales the image up to fill the full grid width. Because the
    // source aspect (muzeum: 1.89) is taller than our target (3.375),
    // the scaled sprite is taller than the box and would normally bleed
    // past the goal line onto the road. We therefore:
    //   1. push the sprite UP by half the vertical overflow, so the
    //      visible window centres on the museum body (door visible,
    //      lawn cropped off the bottom, flag cropped off the top),
    //   2. apply a hard mask at the declared box, killing any pixels
    //      that would otherwise spill onto the active road lanes.
    const bhThemed = FINISH_BUILDING_HEIGHT_PX;
    const byThemed = goalLineY - bhThemed;
    const themed = this.game.theme?.createSprite(
      "frogger.finish.building",
      bw,
      bhThemed,
      { anchorX: 0, anchorY: 0, fitMode: "cover" },
    );

    if (themed) {
      const wrap = new Container();
      themed.x = 0;
      themed.y = byThemed;
      wrap.addChild(themed);

      // Hard clip to the declared box PLUS one extra cell below.
      // The extra row lets the museum's lawn (bottom of muzeum.png)
      // extend down across the WIN ROW only, so the building
      // visually "sits on" its own lawn strip and the next lane
      // immediately below is the first real game lane (a ROAD).
      // The mask still kills anything past that line, so the
      // lawn never bleeds onto the road or vehicles below.
      const maskExtraBelow = cs;
      const mask = new Graphics();
      mask.rect(0, byThemed, bw, bhThemed + maskExtraBelow);
      mask.fill(0xffffff);
      wrap.addChild(mask);
      wrap.mask = mask;

      // The sprite's final scale is only known once its texture has
      // loaded (createSprite is async). We poll for the correct size
      // and then bias the visible window downward so the door — which
      // sits in the lower half of muzeum.png — stays clearly in view
      // while the top (flag/roof) and bottom (lawn) get cropped off.
      this._finishSprite = themed;
      this._finishBoxTop = byThemed;
      this._finishBoxH = bhThemed;
      this._finishMaskExtraBelow = maskExtraBelow;
      this._finishPositioned = false;
      this._positionFinishSprite();

      const gateW = cs * 3;
      const gateH = cs;
      const gateSprite = this.game.theme?.createSprite(
        "frogger.finish.gate",
        gateW,
        gateH,
        {
          anchorX: 0,
          anchorY: 0,
          fitMode: "contain",
        },
      );
      if (gateSprite) {
        gateSprite.x = (bw - gateW) / 2;
        gateSprite.y = goalLineY - gateH;
        wrap.addChild(gateSprite);
      }
      wrap.x = this._originX;
      // Add ON TOP of rows/vehicles so no lane strip can ever bleed in
      // front of the museum's base. The player is added AFTER this call
      // in init() so it still walks ON TOP of the building at the win.
      this._worldContainer.addChild(wrap);
      this._finishGfx = wrap;
      return;
    }

    // No theme texture — fall back to the original procedural building (3 cells tall).
    const bh = cs * 3;
    const bx = 0;
    const by = goalLineY - bh;
    const g = new Graphics();

    g.roundRect(bx + 4, by + 4, bw - 8, bh - 8, 10);
    g.fill(0x7c3aed);
    g.stroke({ color: 0xa78bfa, width: 3 });

    g.roundRect(bx + bw * 0.35, by + bh * 0.15, bw * 0.3, bh * 0.35, 6);
    g.fill(0xfbbf24);

    g.moveTo(bx + bw * 0.2, by + bh * 0.2);
    g.lineTo(bx + bw * 0.5, by - cs * 0.5);
    g.lineTo(bx + bw * 0.8, by + bh * 0.2);
    g.closePath();
    g.fill(0x6d28d9);
    g.stroke({ color: 0xa78bfa, width: 2 });

    const gateW = cs * 3;
    const gateH = cs;
    const gateSprite = this.game.theme?.createSprite(
      "frogger.finish.gate",
      gateW,
      gateH,
      {
        anchorX: 0,
        anchorY: 0,
        fitMode: "contain",
      },
    );
    if (gateSprite) {
      gateSprite.x = bx + (bw - gateW) / 2;
      gateSprite.y = by - cs * 0.45;
      g.addChild(gateSprite);
    }

    const style = new TextStyle({
      fontFamily: "Arial",
      fontSize: 32,
      fontWeight: "bold",
      fill: "#ffffff",
      dropShadow: { color: "#000000", distance: 2, blur: 4 },
    });
    const label = new Text({ text: t("frogger.finish"), style });
    label.anchor.set(0.5);
    label.x = bx + bw / 2;
    label.y = by + bh * 0.65;
    g.addChild(label);

    g.x = this._originX;
    this._worldContainer.addChild(g);
    this._finishGfx = g;
  }

  /**
   * Position the museum sprite once its texture has loaded. The sprite's
   * BOTTOM is anchored to the bottom of the mask (= bottom of the win
   * row), so the lawn portion of muzeum.png always sits cleanly on the
   * win row. The sprite's TOP extends upward by its full rendered height
   * — for the default muzeum (1562x826) at the current grid width that
   * is exactly the mask height (930 + 70 = 1000), so nothing is cropped
   * top OR bottom. For other aspect ratios, anchoring at the bottom
   * means any overflow crops off the TOP (sky above the building),
   * never the building itself.
   */
  _positionFinishSprite() {
    if (this._finishPositioned || !this._finishSprite) return;
    const renderedH = this._finishSprite.height;
    if (!renderedH || renderedH <= 1) return; // texture still loading
    const maskBottom =
      this._finishBoxTop + this._finishBoxH + this._finishMaskExtraBelow;
    this._finishSprite.x = 0;
    this._finishSprite.y = maskBottom - renderedH;
    this._finishPositioned = true;
  }

  _onWin() {
    this._won = true;
    this._hudContainer.visible = false;
    // Celebratory chime the instant the balloon reaches the goal
    // building and the fly-into-the-door cinematic begins.
    this._audio?.playOneShot(BALLOON_END_SFX_URL, 0.9);
    if (this._skyLayer?.startSunSpin) this._skyLayer.startSunSpin(0.035);
    this._startWinFlight();
  }

  /**
   * Kick off the "deflating balloon spirals into the museum door" cinematic.
   * The actual win overlay (and confetti) is deferred until the animation
   * resolves in {@link _updateWinFlight}.
   */
  _startWinFlight() {
    const cs = FROGGER_CONFIG.CELL_SIZE;
    const ps = FROGGER_CONFIG.PLAYER_SIZE;

    const doorWorldX = this._originX + (FROGGER_CONFIG.COLS * cs) / 2;
    // The purple double-door in `muzeum.png` sits in the lower-middle
    // of the building (centre roughly 18% of the building height above
    // its base). The sprite is now anchored bottom-flush against the
    // win row (see `_positionFinishSprite`), so we mirror that anchor
    // here: door world Y = (building bottom) - 0.18 * (building height).
    const doorWorldY = -this._goalRow * cs - FINISH_BUILDING_HEIGHT_PX * 0.18;

    const d = this._player?.display;
    if (!d) return;

    // Switch to centered pivot so scaling shrinks toward the balloon's
    // middle rather than its top-left corner. We compensate the position
    // so the visual does not jump.
    const startX = d.x + ps / 2;
    const startY = d.y + ps / 2;
    d.pivot.set(ps / 2, ps / 2);
    d.x = startX;
    d.y = startY;

    const dx = startX - doorWorldX;

    this._winFlight = {
      t: 0,
      duration: 130,
      startX,
      startY,
      endX: doorWorldX,
      endY: doorWorldY,
      swayAmp: Math.max(70, Math.abs(dx) * 0.6 + 50),
      loops: 2.25,
      done: false,
    };
  }

  _updateWinFlight(delta) {
    const fl = this._winFlight;
    if (!fl || fl.done) return;

    fl.t += delta / fl.duration;
    const tRaw = fl.t;
    const t = Math.min(1, tRaw);

    const ease = t * t * (3 - 2 * t);
    const baseX = fl.startX + (fl.endX - fl.startX) * ease;
    const baseY = fl.startY + (fl.endY - fl.startY) * ease;

    const angle = t * fl.loops * Math.PI * 2;
    const radiusDecay = Math.pow(1 - t, 1.3);
    const ampX = fl.swayAmp * radiusDecay;
    const ampY = fl.swayAmp * 0.55 * radiusDecay;
    const offsetX = Math.sin(angle) * ampX;
    const offsetY = Math.cos(angle * 1.35) * ampY;

    const d = this._player.display;
    d.x = baseX + offsetX;
    d.y = baseY + offsetY;

    const scale = Math.max(0, 1 - Math.pow(t, 0.85));
    d.scale.set(scale);

    d.rotation = Math.sin(angle * 0.85) * 0.35 * (1 - t * 0.5);

    const fadeStart = 0.7;
    if (t > fadeStart) {
      d.alpha = Math.max(0, 1 - (t - fadeStart) / (1 - fadeStart));
    } else {
      d.alpha = 1;
    }

    if (t >= 1) {
      fl.done = true;
      d.visible = false;
      this._winFlight = null;
      this._showWinOverlay();
    }
  }

  _showWinOverlay() {
    let finalScore = this._score + this._lives * 50;
    if (this._score < this._finishScoreGate) {
      finalScore = Math.floor(finalScore / 2);
    }

    partyMode();
    // Shared cross-game victory jingle on the winner screen (ducks the
    // looping ambience so it plays clean).
    this._audio?.playFinished();

    this._winOverlay = new LevelCompleteOverlay();
    this._winOverlay.show(this.game.width, this.game.height, {
      title: t("frogger.you_win"),
      lines: [
        t("frogger.win_time", { v: this._formatTime(this._elapsedTime) }),
        t("frogger.win_rows", { v: this._highestRow }),
        t("frogger.win_coins", { v: this._coinsCollected }),
        t("frogger.win_stars", { v: this._starsCollected }),
        t("frogger.win_lives", { v: this._lives }),
      ],
      score: finalScore,
      scoreLabel: t("common.total_score"),
      onContinue: () => {
        this._showScoreOverlay(finalScore);
      },
    });
    this.container.addChild(this._winOverlay.container);
  }

  _showScoreOverlay(finalScore) {
    if (this._winOverlay) {
      this._winOverlay.destroy();
      this._winOverlay = null;
    }
    this._scoreOverlay = new ScoreSubmitOverlay();
    this._scoreOverlay.show({
      game: "frogger",
      score: finalScore,
      outcome: "win",
      meta: {
        time: this._formatTime(this._elapsedTime),
        rows: this._highestRow,
        coins: this._coinsCollected,
        stars: this._starsCollected,
        lives: this._lives,
        mode: this._mode,
      },
      theme: this.game.theme,
      onDone: () => {
        this.game.sceneManager.switchScene("menu");
      },
    });
  }

  _updateHud() {
    if (!this._topBar) return;

    const rowValue =
      this._mode === "fixed"
        ? `${this._highestRow}/${this._goalRow}`
        : String(this._highestRow);

    const timeValue = this._timeAttack
      ? this._formatTime(Math.max(0, this._timeAttackRemaining / 60))
      : this._formatTime(this._elapsedTime);

    this._topBar.update({
      score: this._score,
      row: rowValue,
      lives: this._lives,
      time: timeValue,
    });

    if (this._slowTimeText) {
      if (this._slowTimeRemaining > 0) {
        const secs = Math.ceil(this._slowTimeRemaining / 60);
        this._slowTimeText.text = t("frogger.ability_slow", { s: secs });
        this._slowTimeText.visible = true;
      } else {
        this._slowTimeText.visible = false;
      }
    }
  }

  _formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  }

  // ── util ───────────────────────────────────────────

  _randInt(min, max) {
    return min + Math.floor(Math.random() * (max - min + 1));
  }
}
